import { getClassNames } from '@/utils/helpers';
import { IconButton } from '@yaradigitallabs/ahua-react';
import { useEffect, type ReactNode } from 'react';

type DrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  position?: 'left' | 'right';
  widthClass?: string;
  showOverlay?: boolean;
  onBack?: () => void;
  footer?: ReactNode;
  withSeparator?: boolean;
};

const Drawer = ({
  isOpen,
  onClose,
  children,
  title,
  position = 'right',
  widthClass = 'w-96',
  showOverlay = true,
  onBack,
  footer,
  withSeparator = true,
}: DrawerProps) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Optional: prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  return (
    <div
      className={getClassNames(
        'absolute inset-0 z-50',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none'
      )}
      aria-hidden={!isOpen}
    >
      {/* Backdrop */}
      {showOverlay && (
        <button
          onClick={onClose}
          className={getClassNames(
            'absolute inset-0 bg-black/50 transition-opacity duration-300',
            isOpen ? 'opacity-100' : 'opacity-0'
          )}
        />
      )}

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'drawer-title' : undefined}
        className={getClassNames(
          'absolute top-0 h-full bg-white shadow-xl transition-transform duration-300 ease-in-out border-t border-[#0f0f0f1a]',
          widthClass,
          position === 'right' ? 'right-0' : 'left-0',
          position === 'right'
            ? isOpen
              ? 'translate-x-0'
              : 'translate-x-full'
            : isOpen
              ? 'translate-x-0'
              : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {title && (
            <header
              className={getClassNames(
                'flex items-center justify-between px-4 py-2',
                withSeparator ? 'border-b border-[#0f0f0f1a]' : ''
              )}
            >
              <div className="flex items-center gap-2">
                {onBack && (
                  <IconButton
                    size="xs"
                    colorConcept="brand"
                    icon="ArrowLeft"
                    onClick={onBack}
                    title="Go back"
                  />
                )}
                {title && (
                  <h2 id="drawer-title" className="text-lg font-semibold">
                    {title}
                  </h2>
                )}
              </div>
              <IconButton
                size="xs"
                colorConcept="brand"
                icon="Clear"
                onClick={onClose}
                aria-label="Close drawer"
                title="Close drawer"
              />
            </header>
          )}
          <div className="flex-1 overflow-y-auto p-4">{children}</div>
          {footer !== undefined && footer !== null && (
            <div
              className={getClassNames(
                'p-4',
                withSeparator ? 'border-t border-[#0f0f0f1a]' : ''
              )}
            >
              {footer}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default Drawer;
